import { FC } from 'react';
import { ErrorLike } from '@apollo/client';
import { Chip, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import moment from 'moment-timezone';
import {
    Appointment,
    Diagnosis,
    InsuranceClaim,
    Payout,
    Pronoun,
    State,
    User,
} from '../../@types/common.types';
import { AssessmentTypeProperties } from '../../constants/assessment.constants';
import {
    AppointmentDxStatus,
    AppointmentStatus,
    AssessmentType,
    ClientStatus,
    CoverageType,
    FaxJobHistoryStatus,
    Gender,
    GroupSessionStatus,
    OrganizationMemberStatus,
    OrganizationStatus,
    PaymentStatus,
    ProviderRecruitingStatus,
    SessionType,
    SessionMode,
} from '../../enums/shared.enums';
import useLocales from '../../hooks/useLocales';
import { formatStringToKey } from '../../utils/locales';
import { toProperCase } from '../../utils/names';
import {
    APPOINTMENT_STATUS_TOOLTIPS,
    CLAIM_STATUS_TOOLTIPS,
    CLIENT_STATUS_TOOLTIPS,
    DX_STATUS_TOOLTIPS,
    FAX_STATUS_TOOLTIPS,
    FULL_STATUSES,
    getAppointmentStatusLabelColor,
    getClaimStatusLabelColor,
    getClientStatusLabelColor,
    getDxStatusFromDx,
    getDxStatusLabelColor,
    getFaxStatusLabelColor,
    getGroupSessionStatusLabelColor,
    getOrganizationStatusLabel,
    getOrgMemberStatusLabelColor,
    getPaymentStatusLabelColor,
    getPayoutStatusLabelColor,
    ORG_MEMBER_STATUS_TOOLTIPS,
    ORG_STATUS_TOOLTIPS,
    PAYOUT_STATUS_TOOLTIPS,
} from '../../utils/statuses';
import Label from '../Label';
import ErrorRetry from '../ErrorRetry';
import Iconify from '../Iconify';
import { SESSION_MODE_ICON_MAP, sessionModeLabelKey } from '../appointment/SessionModeIndicator';
import useAuth from '../../hooks/useAuth';

// TODO: Also, we can probably combine these into one component

// TODO: Probably add functions and constants here

interface AppointmentStatusProps {
    status: AppointmentStatus;
}
export function AppointmentStatusLabel({ status }: AppointmentStatusProps) {
    const { translate } = useLocales();
    const tooltip = translate(APPOINTMENT_STATUS_TOOLTIPS[status]);
    return (
        <Tooltip title={tooltip}>
            <span>
                <Label color={getAppointmentStatusLabelColor(status) as any}>
                    {translate(FULL_STATUSES[status])}
                </Label>
            </span>
        </Tooltip>
    );
}

interface GroupSessionStatusLabelProps {
    status: GroupSessionStatus;
}
export function GroupSessionStatusLabel({ status }: GroupSessionStatusLabelProps) {
    const { translate } = useLocales();
    return (
        <Label color={getGroupSessionStatusLabelColor(status) as any}>
            {translate(FULL_STATUSES[status])}
        </Label>
    );
}

export function AppointmentStatusChip({ status }: AppointmentStatusProps) {
    const { translate } = useLocales();

    const theme = useTheme();
    const colorKey = getAppointmentStatusLabelColor(status);

    const paletteColor =
        colorKey !== 'default' ? (theme.palette as any)[colorKey]?.main : theme.palette.grey[800];
    const tooltip = translate(APPOINTMENT_STATUS_TOOLTIPS[status]);

    return (
        <Tooltip title={tooltip}>
            <Chip
                label={translate(FULL_STATUSES[status])}
                size="medium"
                sx={{
                    borderRadius: '10px',
                    color: paletteColor,
                    backgroundColor: alpha(paletteColor, 0.16),
                    fontWeight: theme.typography.fontWeightMedium,
                }}
            />
        </Tooltip>
    );
}

export function GroupSessionStatusChip({ status }: GroupSessionStatusLabelProps) {
    const { translate } = useLocales();
    const theme = useTheme();

    const colorKey = getGroupSessionStatusLabelColor(status);
    const paletteColor =
        colorKey !== 'default' ? (theme.palette as any)[colorKey]?.main : theme.palette.grey[800];

    return (
        <Chip
            label={translate(FULL_STATUSES[status])}
            size="medium"
            sx={{
                borderRadius: '10px',
                color: paletteColor,
                backgroundColor: alpha(paletteColor, 0.16),
                fontWeight: theme.typography.fontWeightMedium,
            }}
        />
    );
}

interface ClaimStatusProps {
    claim: InsuranceClaim;
}
export function ClaimStatusLabel({ claim }: ClaimStatusProps) {
    const { translate } = useLocales();
    const status = claim.status;
    const tooltipKey = translate(CLAIM_STATUS_TOOLTIPS[status]);
    return (
        <Tooltip title={tooltipKey}>
            <span>
                <Label color={getClaimStatusLabelColor(status) as any}>{status}</Label>
            </span>
        </Tooltip>
    );
}

interface PayoutStatusProps {
    payout: Payout;
}
export function PayoutStatusLabel({ payout }: PayoutStatusProps) {
    const { translate } = useLocales();
    const status = payout.status;
    const tooltipKey = PAYOUT_STATUS_TOOLTIPS[status as keyof typeof PAYOUT_STATUS_TOOLTIPS];
    const tooltip = tooltipKey ? translate(tooltipKey) : '';
    return (
        <Tooltip title={tooltip}>
            <span>
                <Label color={getPayoutStatusLabelColor(status) as any}>
                    {translate('components.labels.status.payout.' + formatStringToKey(status), {
                        defaultValue: toProperCase(status, '_'),
                    })}
                </Label>
            </span>
        </Tooltip>
    );
}

interface DxStatusProps {
    diagnosis: Diagnosis;
    coverageType?: CoverageType | null;
}
export function DxStatusLabel({ diagnosis, coverageType }: DxStatusProps) {
    const { translate } = useLocales();

    const status: AppointmentDxStatus = getDxStatusFromDx(diagnosis, coverageType);
    const tooltip = translate(DX_STATUS_TOOLTIPS[status]);
    return (
        <Tooltip title={tooltip}>
            <span>
                <Label color={getDxStatusLabelColor(status) as any}>{status}</Label>
            </span>
        </Tooltip>
    );
}

interface DxCodeStatusProps {
    diagnosis: Diagnosis;
    appointment: Appointment;
}
export function DxCodeStatusLabel({ diagnosis, appointment }: DxCodeStatusProps) {
    const { translate } = useLocales();

    if (!diagnosis || appointment.coverageType === CoverageType.EAP) {
        return <></>;
    }

    const isSubmitted = diagnosis.areCodesLocked;
    const status = isSubmitted
        ? translate('components.claim.label.areCodesLocked.submitted')
        : translate('components.claim.label.areCodesLocked.unsubmitted');
    const tooltip = isSubmitted
        ? translate('components.claim.label.areCodesLocked.submittedTooltip')
        : translate('components.claim.label.areCodesLocked.unsubmittedTooltip');

    return (
        <Tooltip title={tooltip}>
            <span data-testid="diagnosis-code-status">
                <Label color={isSubmitted ? 'success' : 'error'}>{status}</Label>
            </span>
        </Tooltip>
    );
}

interface DxNotesStatusProps {
    diagnosis: Diagnosis;
}
export function DxNotesStatusLabel({ diagnosis }: DxNotesStatusProps) {
    const { translate } = useLocales();

    if (!diagnosis) {
        return <></>;
    }

    let status, tooltip, color;
    if (!diagnosis.areNotesProcessed) {
        status = translate('components.claim.label.areNotesProcessed.unprocessed');
        tooltip = translate('components.claim.label.areNotesProcessed.unprocessedTooltip');
        color = 'warning';
    } else if (diagnosis.areNotesLocked) {
        status = translate('components.claim.label.areNotesProcessed.signed');
        tooltip = translate('components.claim.label.areNotesProcessed.signedTooltip');
        color = 'success';
    } else {
        status = translate('components.claim.label.areNotesProcessed.unsigned');
        tooltip = translate('components.claim.label.areNotesProcessed.unsignedTooltip');
        color = 'error';
    }

    return (
        <Tooltip title={tooltip}>
            <span data-testid="diagnosis-note-status">
                <Label color={color as any}>{status}</Label>
            </span>
        </Tooltip>
    );
}

// TODO: We can combing these into one component

interface PaymentStatusProps {
    status: PaymentStatus;
}
export function PaymentStatusLabel({ status }: PaymentStatusProps) {
    const { translate } = useLocales();
    return (
        <Label color={getPaymentStatusLabelColor(status) as any}>
            {translate(`constants.shared.paymentStatus.${status}`)}
        </Label>
    );
}

interface CoverageTypeProps {
    coverageType: CoverageType;
}
export function CoverageTypeLabel({ coverageType }: CoverageTypeProps) {
    let color = 'success';
    let label = 'S';
    if (coverageType === CoverageType.INSURANCE) {
        color = 'info';
        label = 'I';
    } else if (coverageType === CoverageType.EAP) {
        color = 'warning';
        label = 'E';
    }
    return (
        <Tooltip title={coverageType}>
            <span>
                <Label color={color as any}>{label}</Label>
            </span>
        </Tooltip>
    );
}

interface SessionTypeProps {
    sessionType: SessionType;
}
export function SessionTypeLabel({ sessionType }: SessionTypeProps) {
    const { translate } = useLocales();

    let color = 'primary';
    if (sessionType === SessionType.COUPLES) {
        color = 'info';
    } else if (sessionType === SessionType.FAMILY) {
        color = 'warning';
    }
    return (
        <Label color={color as any}>
            {translate(`components.labels.status.sessionType.${sessionType?.toLowerCase()}`)}
        </Label>
    );
}

interface SessionModeProps {
    sessionMode?: SessionMode | null;
}
export function SessionModeLabel({ sessionMode }: SessionModeProps) {
    const { translate } = useLocales();

    if (!sessionMode) return null;

    return (
        <Label
            color="default"
            startIcon={<Iconify icon={SESSION_MODE_ICON_MAP[sessionMode]} width={14} height={14} />}
        >
            {translate(sessionModeLabelKey(sessionMode))}
        </Label>
    );
}

interface RegProps {
    isRegistrationComplete: boolean;
    full?: boolean;
}
export function RegistrationStatusLabel({ isRegistrationComplete, full }: RegProps) {
    const { translate } = useLocales();
    const affirmative = full
        ? translate('components.labels.status.registration.complete')
        : translate('components.labels.status.registration.completeShort');
    const negative = full
        ? translate('components.labels.status.registration.incomplete')
        : translate('components.labels.status.registration.incompleteShort');
    return (
        <Tooltip
            title={
                isRegistrationComplete
                    ? translate('components.labels.status.registration.complete')
                    : translate('components.labels.status.registration.incomplete')
            }
        >
            <span>
                <Label color={isRegistrationComplete ? 'success' : 'warning'}>
                    {isRegistrationComplete ? affirmative : negative}
                </Label>
            </span>
        </Tooltip>
    );
}

interface IntakeProps {
    isIntakeComplete: boolean;
}
export function IntakeStatusLabel({ isIntakeComplete }: IntakeProps) {
    const { translate } = useLocales();
    return (
        <Tooltip
            title={
                isIntakeComplete
                    ? translate('components.labels.status.intake.complete')
                    : translate('components.labels.status.intake.incomplete')
            }
        >
            <span>
                <Label color={isIntakeComplete ? 'info' : 'warning'}>
                    {isIntakeComplete
                        ? translate('components.labels.status.intake.complete')
                        : translate('components.labels.status.intake.incomplete')}
                </Label>
            </span>
        </Tooltip>
    );
}

interface MinorConsentProps {
    isMinor: boolean;
    isMinorConsentComplete: boolean;
}
export function MinorConsentLabel({ isMinor, isMinorConsentComplete }: MinorConsentProps) {
    const { translate } = useLocales();

    if (!isMinor) {
        return <></>;
    }
    return (
        <Tooltip
            title={
                isMinorConsentComplete
                    ? translate('components.labels.status.minorConsent.complete')
                    : translate('components.labels.status.minorConsent.incomplete')
            }
        >
            <span>
                <Label color={isMinorConsentComplete ? 'info' : 'warning'}>
                    {isMinorConsentComplete
                        ? translate('components.labels.status.minorConsent.complete')
                        : translate('components.labels.status.minorConsent.incomplete')}
                </Label>
            </span>
        </Tooltip>
    );
}

interface PAPLProps {
    isApproved: boolean;
}
export function ProviderApprovalLabel({ isApproved }: PAPLProps) {
    const { translate } = useLocales();
    return (
        <Tooltip
            title={
                isApproved
                    ? translate('components.labels.status.providerApproval.approved')
                    : translate('components.labels.status.providerApproval.unapproved')
            }
        >
            <span>
                <Label color={isApproved ? 'success' : 'error'}>
                    {isApproved
                        ? translate('components.labels.status.providerApproval.approvedShort')
                        : translate('components.labels.status.providerApproval.unapprovedShort')}
                </Label>
            </span>
        </Tooltip>
    );
}

interface EapProps {
    acceptsEapClients: boolean;
}
export function ProviderEapLabel({ acceptsEapClients }: EapProps) {
    const { translate } = useLocales();
    if (!acceptsEapClients) return null;
    return (
        <Tooltip title={translate('components.labels.status.eap.acceptingEapClients')}>
            <span>
                <Label color={'info'}>{'EAP'}</Label>
            </span>
        </Tooltip>
    );
}

interface PBLProps {
    acceptsNewClients: boolean;
    bookingPriority: number;
}
export function ProviderBookingLabel({ acceptsNewClients, bookingPriority }: PBLProps) {
    const { isPayerAdmin } = useAuth();
    const { translate } = useLocales();
    if (acceptsNewClients && bookingPriority == null) return null;
    return (
        <Tooltip
            title={translate(
                acceptsNewClients
                    ? 'components.labels.status.providerBookingLabel.acceptingNewClients'
                    : 'components.labels.status.providerBookingLabel.notAcceptingNewClients'
            )}
        >
            <span>
                <Typography variant={'body2'} color={'textSecondary'}>
                    {!acceptsNewClients ? '🚫' : isPayerAdmin ? '' : bookingPriority}
                </Typography>
            </span>
        </Tooltip>
    );
}

interface UserActiveLabel {
    isActive: boolean;
}
export function UserActiveLabel({ isActive }: UserActiveLabel) {
    const { translate } = useLocales();
    return (
        <Tooltip
            title={
                isActive
                    ? translate('components.labels.status.userActive.active')
                    : translate('components.labels.status.userActive.inactive')
            }
        >
            <span>
                <Label color={isActive ? 'success' : 'warning'}>
                    {isActive
                        ? translate('components.labels.status.userActive.activeShort')
                        : translate('components.labels.status.userActive.inactiveShort')}
                </Label>
            </span>
        </Tooltip>
    );
}

interface ClientStatusProps {
    status: ClientStatus;
}
export function ClientStatusLabel({ status }: ClientStatusProps) {
    const { translate } = useLocales();

    return (
        <Tooltip title={translate(CLIENT_STATUS_TOOLTIPS[status])}>
            <span>
                <Label color={getClientStatusLabelColor(status) as any}>{status}</Label>
            </span>
        </Tooltip>
    );
}

interface OrgMemberStatusLabelProps {
    status: OrganizationMemberStatus;
}
export function OrgMemberStatusLabel({ status }: OrgMemberStatusLabelProps) {
    const { translate } = useLocales();

    return (
        <Tooltip title={translate(ORG_MEMBER_STATUS_TOOLTIPS[status])}>
            <span>
                <Label color={getOrgMemberStatusLabelColor(status) as any}>
                    {translate(
                        'components.labels.status.orgMemberStatus.' + formatStringToKey(status)
                    )}
                </Label>
            </span>
        </Tooltip>
    );
}

interface OrganizationStatusProps {
    status: OrganizationStatus;
}
export function OrganizationStatusLabel({ status }: OrganizationStatusProps) {
    const { translate } = useLocales();

    return (
        <Tooltip title={translate(ORG_STATUS_TOOLTIPS[status])}>
            <span>
                <Label color={getOrganizationStatusLabel(status) as any}>
                    {translate(
                        'components.labels.status.organizations.' + formatStringToKey(status)
                    )}
                </Label>
            </span>
        </Tooltip>
    );
}

interface GenderLabelProps {
    gender: Gender;
}
export function GenderLabel({ gender }: GenderLabelProps) {
    gender = gender ?? '?';
    return (
        <Tooltip title={gender}>
            <span>
                <Label
                    color={
                        gender === Gender.MALE
                            ? 'info'
                            : gender === Gender.FEMALE
                            ? 'error'
                            : 'default'
                    }
                >
                    {gender[0]}
                </Label>
            </span>
        </Tooltip>
    );
}

interface LabelsAndPronounsProps extends GenderLabelProps {
    pronoun: Pronoun;
}
export function LabelsAndPronouns({ gender, pronoun }: LabelsAndPronounsProps) {
    return (
        <Tooltip title={gender}>
            <span>
                <Label
                    color={
                        gender === Gender.MALE
                            ? 'info'
                            : gender === Gender.FEMALE
                            ? 'error'
                            : 'default'
                    }
                >
                    {pronoun?.name && pronoun.name} ({gender[0]})
                </Label>
            </span>
        </Tooltip>
    );
}
interface AgeLabelProps {
    dateOfBirth: string;
}
export function AgeLabel({ dateOfBirth }: AgeLabelProps) {
    const { translate } = useLocales();
    return (
        <Tooltip
            title={translate('components.labels.status.dob.born', {
                date: moment(dateOfBirth).format('MMMM Do YYYY'),
            })}
        >
            <span>
                <Label color={'info'}>{moment().diff(moment(dateOfBirth), 'years')}</Label>
            </span>
        </Tooltip>
    );
}

export function StateLabel({ state }: { state?: State }) {
    return (
        <Tooltip title={state?.name ?? '?'}>
            <span>
                <Label color={'info'}>{state?.abbreviation ?? '?'}</Label>
            </span>
        </Tooltip>
    );
}

export function AssessmentTypeLabel({ type }: { type: AssessmentType }) {
    const props = AssessmentTypeProperties[type];
    return (
        <Tooltip title={props?.fullName ?? '?'}>
            <Typography variant={'body2'} color={'textSecondary'}>
                {type.toUpperCase()}
            </Typography>
        </Tooltip>
    );
}

export function ReferralLabel({ client }: { client: User }) {
    const { translate } = useLocales();
    const clientProfile = client?.clientProfile;

    if (
        !(clientProfile?.coverageType == CoverageType.EAP) ||
        !clientProfile?.eapOptions?.isFormalReferral
    )
        return null;

    return (
        <Tooltip title={translate('components.labels.status.eap.isReferralTooltip')}>
            <span>
                <Label color={'success'}>
                    {translate('components.labels.status.eap.isReferralLabel')}
                </Label>
            </span>
        </Tooltip>
    );
}

interface EapSessionsLabelProps {
    remaining?: number;
    expiration?: string | null;
    isExpired?: boolean;
    timezone?: string;
    error?: ErrorLike;
    onRetry?: () => void;
}

export const EapSessionsLabel: FC<EapSessionsLabelProps> = ({
    remaining,
    expiration,
    isExpired,
    timezone,
    error,
    onRetry,
}) => {
    const { translate } = useLocales();

    if (error && remaining === undefined) {
        return <ErrorRetry error={error} onRetry={onRetry} sx={{ flex: 'none', p: 0.5 }} />;
    }

    if (remaining === undefined) return null;

    return (
        <Label color={isExpired ? 'warning' : 'info'}>
            {translate('components.labels.status.eap.sessionsLeft', {
                count: Math.max(remaining, 0),
            })}
            {isExpired && expiration && (
                <Typography variant="inherit" component="span" sx={{ ml: 0.5, color: 'warning.main' }}>
                    {translate('components.labels.status.eap.expiredOn', {
                        date: moment
                            .tz(expiration, timezone || moment.tz.guess())
                            .format('DD MMM YYYY'),
                    })}
                </Typography>
            )}
        </Label>
    );
};

export function SafetySensitiveLabel({ client }: { client: User }) {
    const { translate } = useLocales();
    const clientProfile = client?.clientProfile;

    if (
        !(clientProfile?.coverageType == CoverageType.EAP) ||
        !clientProfile?.eapOptions?.isSafetySensitive
    )
        return null;

    return (
        <Tooltip title={translate('components.labels.status.eap.isSafetySensitiveTooltip')}>
            <span>
                <Label color={'warning'}>
                    {translate('components.labels.status.eap.isSafetySensitiveLabel')}
                </Label>
            </span>
        </Tooltip>
    );
}

interface FaxStatusProps {
    status: FaxJobHistoryStatus;
}
export function FaxStatusLabel({ status }: FaxStatusProps) {
    const tooltip = FAX_STATUS_TOOLTIPS[status];
    return (
        <Tooltip title={tooltip}>
            <span>
                <Label color={getFaxStatusLabelColor(status) as any}>
                    {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
                </Label>
            </span>
        </Tooltip>
    );
}

const PROVIDER_RECRUITING_STATUS_COLORS: Record<
    ProviderRecruitingStatus,
    'default' | 'primary' | 'secondary' | 'info' | 'success' | 'warning' | 'error'
> = {
    [ProviderRecruitingStatus.INTERESTED]: 'info',
    [ProviderRecruitingStatus.INTRO_CALL_SCHEDULED]: 'info',
    [ProviderRecruitingStatus.INTRO_CALL_COMPLETED]: 'primary',
    [ProviderRecruitingStatus.INTERVIEW_SCHEDULED]: 'primary',
    [ProviderRecruitingStatus.CONTRACT_SENT]: 'warning',
    [ProviderRecruitingStatus.CONTRACT_SIGNED]: 'success',
    [ProviderRecruitingStatus.PAUSED]: 'default',
    [ProviderRecruitingStatus.REJECTED]: 'error',
    // Warning, not error: the clinician declined but is recoverable via
    // objection handling, unlike REJECTED which is a disqualification.
    [ProviderRecruitingStatus.NOT_INTERESTED]: 'warning',
};

/** "IntroCallScheduled" -> "Intro Call Scheduled" */
function splitPascalCase(value: string): string {
    return value.replace(/([a-z])([A-Z])/g, '$1 $2');
}

interface ProviderRecruitingStatusProps {
    status: ProviderRecruitingStatus | null | undefined;
}
export function ProviderRecruitingStatusLabel({ status }: ProviderRecruitingStatusProps) {
    if (!status) return null;
    return (
        <Label color={PROVIDER_RECRUITING_STATUS_COLORS[status]}>{splitPascalCase(status)}</Label>
    );
}
